import { Bot, Hash, Mic, Paperclip, Smile, Zap } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmCustomerIdentityModal } from '../../../components/ConfirmCustomerIdentityModal';
import { EstablishLinkModal } from '../../../components/EstablishLinkModal';
import { useCannedVariables } from '../../../hooks/use-canned-responses';
import { useCapability } from '../../../hooks/use-capability';
import { ApiError, type CannedResponseWire } from '../../../lib/api-client';
import { useAuthStore } from '../../../stores/auth-store';
import { isReopenableStatus, isTerminalStatus } from '../format';
import { useAdversarial } from '../hooks/use-adversarial';
import { useAISuggestion } from '../hooks/use-ai-suggestion';
import { useCloseConversationFlow } from '../hooks/use-close-conversation-flow';
import { useConflictAlerts } from '../hooks/use-conflict-alerts';
import { useProtectionCheck } from '../hooks/use-protection-check';
import { useSendMessage } from '../hooks/use-send-message';
import { useSequence } from '../hooks/use-sequence';
import { useSetOperationMode } from '../hooks/use-set-operation-mode';
import { useWindow24h } from '../hooks/use-window-24h';

import { AdversarialBadge } from './AdversarialBadge';
import { CannedChipsRow } from './canned/CannedChipsRow';
import { CannedResponsePicker } from './canned/CannedResponsePicker';
import { ReviewQueue } from './canned/ReviewQueue';
import { ChecklistAgentBanner } from './ChecklistAgentBanner';
import { ClassificationSeal } from './ClassificationSeal';
import { ConflictWarningBanner } from './ConflictWarningBanner';
import { ConversationCloseModal } from './ConversationCloseModal';
import { DelegateStepsModal } from './DelegateStepsModal';
import { DocumentPicker } from './DocumentPicker';
import { ProtectionBlockModal } from './ProtectionBlockModal';
import { ProtectionWarningBanner } from './ProtectionWarningBanner';
import { SequenceBadge } from './SequenceBadge';
import { SequenceBanner } from './SequenceBanner';
import { SuggestionChipsRow } from './SuggestionChipsRow';
import { TemplateSelectorModal, type TemplateSendRequest } from './TemplateSelectorModal';
import { Window24hBanner } from './Window24hBanner';

import type {
  AttachedDocument,
  ConversationDetail,
  DocumentSummary,
  SendMessageBody,
} from '../../../lib/types';
import type { ProtectionValidation } from '../types/protection.types';
import type { LucideIcon } from 'lucide-react';

const MAX_CHARS = 4096;

/**
 * Composer Enxuto v2 — pílula compacta + textarea + ações inline.
 *
 * Layout fiel ao Brief 2A V2.3 (Composer Enxuto v2 — refinado pós-R1).
 *
 * **Fatia 6 PR-A ativa:** envio funcional via `useSendMessage`. Optimistic
 * update + Idempotency-Key + Cmd/Ctrl+Enter + Esc clear + contador 4096.
 *
 * **Fatia 7 ativa:** sugestão IA fantasma no textarea via `useAISuggestion`.
 * Tab aceita (preenche + registra `accepted`); Esc com sugestão pendente
 * descarta (registra `rejected`); digitar por cima descarta sugestão
 * (registra `ignored`); Cmd/Ctrl+Enter envia (registra `accepted` antes
 * do send se sugestão estava ativa).
 *
 * **PR-B Fatia 7 ativa:** indicador adversarial no header do Composer
 * (`<AdversarialBadge />`) + banner discreto warning/blocked
 * (`<ConflictWarningBanner />`) materializam Items 1+3 do diferimento da
 * Fatia 7. Hoje (NetComet, `bond=null` no `ConversationDetail`) badge não
 * renderiza — comportamento canônico. Quando bond materializar em fatia
 * futura, badge ativa sem mudança aqui.
 *
 * Diferimentos:
 *   - Persona+Playbook configuração inline → fatia futura
 *   - Quick replies (snippets/playbook) → Fatia 6 PR-B
 *   - Anexar Document funcional → Fatia 6 PR-B
 *
 * Estados terminais (closed) ou H_c bloqueiam composer com banner próprio.
 */
export function Composer({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  // Item 236 (29/Mai/2026) — guardar DocumentSummary completo (não apenas
  // id+name) para montar o card otimista no bubble outbound antes do refetch.
  const [attachedDocument, setAttachedDocument] = useState<DocumentSummary | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // bondId é string|null em ConversationDetail (via ConversationListItem).
  // DocumentPicker aceita string|undefined — converter null para undefined.
  const bondId = conversation.bondId ?? undefined;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { send, mutation } = useSendMessage(conversation.id, currentUserId);
  const aiSuggestion = useAISuggestion(conversation.id);

  // T9 Fase 1 «um cérebro só» — chips de conduta do dry-run do conductor.
  // Os ACIONÁVEIS apontam aos fluxos EXISTENTES da tela (nenhum caminho novo):
  //  - `otp-challenge` → mesmo `ConfirmCustomerIdentityModal` do popover de
  //    identidade (Item 391-B); só acionável com a cap + identidade presente
  //    ainda não-verificada (a mesma regra `showConfirmIdentity` do popover);
  //  - `close` → mesmo fluxo Encerrar do header (hook compartilhado
  //    `useCloseConversationFlow`); só acionável fora de estado terminal.
  // Sem a condição, o chip degrada a badge informativa (não finge clicável).
  const canConfirmIdentity = useCapability('customer-registry-confirm');
  const canFollowup = useCapability('initiate-proactive-conversation');
  const [confirmTitularOpen, setConfirmTitularOpen] = useState(false);
  const closeFlow = useCloseConversationFlow(conversation.id);
  const chipOtpAvailable =
    canConfirmIdentity && conversation.identity != null && conversation.identity.verified !== true;
  // F2-b — chip `link-customer` → mesmo EstablishLinkModal do empty-state da aba
  // Cliente (F2-a). Só acionável com a cap `customer-link-establish` (opt-in); sem
  // ela, o chip degrada a badge estática (regra de honestidade do SuggestionChipsRow).
  // O modal se auto-gateia por CapabilityGate.
  const canEstablishLink = useCapability('customer-link-establish');
  const [establishLinkOpen, setEstablishLinkOpen] = useState(false);

  // S2 Task 8 — Respostas Prontas: picker "/" + botão ⚡ + chips reais do
  // playbook ativo (pills fake mortas). `stagedResponse` é o estado que a
  // Task 9 consome para render a `<ReviewQueue>` real (fila de revisão)
  // logo acima do `.composer-r7b__box`.
  const hasCannedCap = useCapability('canned-response-use');
  const [cannedPickerOpen, setCannedPickerOpen] = useState(false);
  const [stagedResponse, setStagedResponse] = useState<CannedResponseWire | null>(null);
  // S2 Task 9 — variáveis camada 1/2 resolvidas pelo Engine para a fila de
  // revisão. Gate-the-fetch (`feedback_gatear_fetch_nao_so_render`): só busca
  // com a fila aberta. O hook roda INCONDICIONALMENTE (regra dos hooks — o
  // Composer não retorna cedo o suficiente para pular esta linha em nenhum
  // modo não-terminal/não-H_c/não-conflito-crítico); `enabled` faz o trabalho.
  const cannedVariablesQ = useCannedVariables(conversation.id, stagedResponse !== null);
  // Fix pós-review T9 (Important #1) — com uma fila EM REVISÃO (edições,
  // estado de falha parcial), selecionar outra resposta NÃO pode remontar a
  // fila (o remount via `key` destruiria tudo silenciosamente). Enquanto a
  // fila está aberta, selecionar QUALQUER resposta vira no-op; resposta
  // DIFERENTE mostra o aviso `role="status"` acima da fila.
  const [stagedConflictNotice, setStagedConflictNotice] = useState(false);
  const activePlaybookId = conversation.playbook?.id ?? null;
  // Enquanto o atendente digita "/…", o draft já carrega o texto — a query
  // do picker deriva dele (sem estado redundante); aberto via botão/chip
  // "Biblioteca…" o draft normalmente não começa com "/", então cai em modo
  // navegação (query vazia), como o spec pede.
  const cannedPickerQuery = cannedPickerOpen && draft.startsWith('/') ? draft.slice(1) : '';
  // Important #2 pós-review (ARIA combobox) — o picker reporta o `id` da
  // option ativa (↑↓) e o textarea o expõe via `aria-activedescendant`.
  const [cannedActiveOptionId, setCannedActiveOptionId] = useState<string | null>(null);

  const handleCannedSelect = useCallback(
    (response: CannedResponseWire) => {
      setCannedPickerOpen(false);
      // Fix pós-review T9 (Important #1) — fila em revisão é INTOCÁVEL:
      // selecionar com fila aberta nunca destrói o estado (edições/falha
      // parcial). Resposta DIFERENTE → aviso; a MESMA → no-op silencioso.
      if (stagedResponse !== null) {
        if (stagedResponse.id !== response.id) setStagedConflictNotice(true);
        textareaRef.current?.focus();
        return;
      }
      // Important #1 pós-review — só limpa o draft quando ele é o TRIGGER do
      // picker ("/…" digitado). Aberto via botão ⚡/chip "Biblioteca…" com
      // texto manual no composer → draft INTOCADO (nunca descartar o que o
      // atendente escreveu).
      if (draft.startsWith('/')) setDraft('');
      setStagedResponse(response);
      textareaRef.current?.focus();
    },
    [draft, stagedResponse],
  );

  // Descartar/enviar a fila limpa o staged E o aviso de conflito junto.
  const dismissStagedResponse = useCallback(() => {
    setStagedResponse(null);
    setStagedConflictNotice(false);
  }, []);

  // PR-B.2 (Item 89) — 4 hooks de contexto da conversa.
  const adversarial = useAdversarial(conversation);
  const sequence = useSequence(conversation);
  const conflictAlerts = useConflictAlerts(conversation);

  // Sprint Protection v1 Agent C (Item 226) — UI Composer pré-envio.
  // Captura validation do response sendOutbound (warn → banner;
  // block → modal). Capability gate `ai-protection-outbound-validate`
  // já aplicado dentro do hook.
  const protection = useProtectionCheck();

  // Item 27 — janela 24h Meta (advisory; o Engine é o enforcer read-time).
  const window24h = useWindow24h(conversation);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const mode = conversation.operationMode.current;
  // §2.8 — modo somente leitura em qualquer estado terminal (resolved,
  // closed-unresolved, archived, closed). Fonte única: isTerminalStatus.
  const isClosed = isTerminalStatus(conversation.status);
  const isHcLocked = mode === 'H_c';

  // S5 (maré baixa produtiva) — deep-link `?delegate=1` do card de sugestão.
  // Consumido AQUI (hooks do Composer rodam em TODOS os modos — em H_c o
  // render retorna o HcLockedBanner ANTES do JSX onde DelegateStepsAction
  // monta, então o param ficaria stale e o modal auto-abriria inesperado
  // quando o operador assumisse depois — NIT 3 da revisão adversarial).
  // Só SINALIZA a abertura quando cap ativa ∧ fora de H_c; consome SEMPRE.
  const hasDelegateStepsCap = useCapability('ai-step-delegation');
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenDelegateSteps, setAutoOpenDelegateSteps] = useState(false);
  useEffect(() => {
    if (searchParams.get('delegate') !== '1') return;
    if (hasDelegateStepsCap && mode !== 'H_c') setAutoOpenDelegateSteps(true);
    const next = new URLSearchParams(searchParams);
    next.delete('delegate');
    setSearchParams(next, { replace: true });
  }, [hasDelegateStepsCap, mode, searchParams, setSearchParams]);

  const trimmed = draft.trim();
  const charsLeft = MAX_CHARS - draft.length;
  // Item 285 (29/Mai/2026) — anexo de documento conta como conteúdo para
  // habilitar o envio. Antes: hasContent só considerava texto trimmed, então
  // user que anexava arquivo sem digitar caption ficava com botão "Enviar"
  // disabled (silenciosamente — sem aviso). Fix: || attachedDocument.
  const hasContent = trimmed.length > 0 || attachedDocument !== null;
  const isOversized = draft.length > MAX_CHARS;
  const isSending = mutation.isPending;
  // Sprint Protection v1 (Item 226) — Modal block aberto desabilita "Enviar".
  const isProtectionBlocking = protection.uiDecision === 'modal';
  const canSend =
    hasContent && !isOversized && !isSending && !isClosed && !isHcLocked && !isProtectionBlocking;

  // Sugestão ready+draft-vazio ATIVA — engloba tanto a sugestão com texto
  // quanto a chip-only (proposedText === ''). Usado para o que faz sentido
  // independente de haver texto: renderizar os chips, marcar "ignored" ao
  // digitar por cima, cancelar via Esc, e não deixar pedir outra sugestão
  // (Sugerir) enquanto esta está ativa.
  const activeSuggestion =
    aiSuggestion.status === 'ready' && aiSuggestion.suggestion && draft === ''
      ? aiSuggestion.suggestion
      : null;

  // Revisão final Fase 1 (Fix 2) — o "ghost text" (overlay + Tab aceita +
  // botões Aceitar/Recusar + strip) só existe quando há TEXTO proposto de
  // fato. Uma sugestão chip-only (`proposedText === ''`) não deve virar
  // ghost text aceitável por Tab (aceitar vazio era ruído silencioso: draft
  // permanecia vazio e a decisão era marcada 'accepted' sem nada acontecer).
  // Os chips continuam renderizando via `activeSuggestion` acima.
  const ghostSuggestion =
    activeSuggestion && activeSuggestion.proposedText !== '' ? activeSuggestion : null;

  const acceptSuggestion = useCallback(() => {
    if (!aiSuggestion.suggestion) return;
    const text = aiSuggestion.suggestion.proposedText;
    setDraft(text);
    void aiSuggestion.recordDecision('accepted', text);
  }, [aiSuggestion]);

  // FE-1 (Brief 2A spec §4.3) — recusar sugestão: registra `rejected` + limpa.
  // Reusado pelo botão "Recusar" da toolbar e pelo atalho Esc.
  const rejectSuggestion = useCallback(() => {
    void aiSuggestion.recordDecision('rejected');
    setDraft('');
  }, [aiSuggestion]);

  /**
   * Sprint Protection v1 (Item 226) + F1.3 (bloqueio honesto) — captura
   * validation + dispatch.
   *
   * Quando backend retorna envelope com `validation` (warn flow severity
   * low/medium), banner inline aparece via `protection.setValidation`.
   *
   * Quando backend retorna 400 `validation_blocked` (block flow severity
   * high/critical), ApiError chega ao catch — extrai `body.validation` e
   * abre ProtectionBlockModal INFORMATIVO. Mensagem permanece no draft (não
   * limpa) para o atendente editar. F1.3: NÃO há reenvio com override — o
   * bloqueio é HARD no Engine.
   */
  const dispatchSend = useCallback(
    async (
      body: SendMessageBody,
      /** Item 236 — hint optimistic; NÃO vai para o servidor (sanitizado pelo hook). */
      opts?: { attachedDocument: AttachedDocument },
    ): Promise<void> => {
      try {
        const envelope = await send(body, opts);
        if (envelope?.validation !== undefined) {
          // Sucesso com warning — banner inline (severity low/medium).
          protection.setValidation(envelope.validation);
        } else {
          // Sucesso limpo — limpa qualquer validation residual.
          protection.clear();
        }
      } catch (err) {
        // Sprint Protection v1: 400 'validation_blocked' → abre modal informativo.
        if (err instanceof ApiError && err.status === 400) {
          const body400 = err.body as { error?: string; validation?: ProtectionValidation };
          if (body400.error === 'validation_blocked' && body400.validation !== undefined) {
            // Modal block — mensagem permanece preservada no input via restoreDraft.
            protection.setValidation(body400.validation);
            // Restaura draft preservando texto original.
            setDraft(body.content);
            return;
          }
        }
        // Outros erros: optimistic vira failed via mutation.onError (Fatia 6 PR-A).
      }
    },
    [send, protection],
  );

  const onSend = useCallback(async () => {
    if (!canSend) return;
    const wasSuggestion = aiSuggestion.suggestion?.proposedText;
    const finalText = trimmed;
    setDraft('');
    if (wasSuggestion !== undefined && wasSuggestion === finalText) {
      // Já foi marcada como accepted no momento do Tab; nada mais a fazer aqui.
    } else if (wasSuggestion !== undefined && wasSuggestion !== finalText) {
      // Atendente aceitou + editou.
      void aiSuggestion.recordDecision('edited', finalText);
    }
    const body: SendMessageBody =
      attachedDocument !== null
        ? { content: finalText, contentType: 'document', documentId: attachedDocument.id }
        : { content: finalText, contentType: 'text' };
    // Item 236 — hint optimistic para o card de anexo aparecer no bubble outbound
    // antes do refetch hidratar `attachedDocument` real (com signedUrl).
    const optimisticHint =
      attachedDocument !== null
        ? { attachedDocument: documentSummaryToAttached(attachedDocument) }
        : undefined;
    await dispatchSend(body, optimisticHint);
    setAttachedDocument(null);
  }, [canSend, dispatchSend, trimmed, aiSuggestion, attachedDocument]);

  /**
   * Sprint Protection v1 (Item 226) + F1.3 — fechar/editar do modal
   * informativo: preserva draft + devolve foco ao composer. NÃO há reenvio
   * com override (bloqueio HARD no Engine).
   */
  const onProtectionCancel = useCallback(() => {
    protection.clear();
    // Foco devolvido naturalmente via Modal DS (focus trap libera). Rascunho
    // preservado — atendente edita e reenvia.
  }, [protection]);

  /**
   * Item 27 — envio como template HSM (janela 24h fechada). `content` é o
   * preview renderizado client-side (bolha otimista fiel); o adapter renderiza
   * `renderedBody` server-side (fonte forense). Reusa dispatchSend (optimistic
   * + Idempotency-Key + Protection pipeline).
   */
  const onSendTemplate = useCallback(
    async (req: TemplateSendRequest) => {
      setTemplateModalOpen(false);
      const body: SendMessageBody = {
        content: req.renderedContent,
        contentType: 'text',
        hsmFallback: { templateName: req.templateName, variables: req.variables },
      };
      await dispatchSend(body);
    },
    [dispatchSend],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // S2 Task 8 — Esc fecha o picker de Respostas Prontas PRESERVANDO o
      // draft (precedência sobre o fallback "Esc limpa" abaixo; SEM isso o
      // mesmo keydown limparia a mensagem que o atendente estava digitando).
      if (e.key === 'Escape' && cannedPickerOpen) {
        e.preventDefault();
        setCannedPickerOpen(false);
        return;
      }
      if (e.key === 'Tab' && ghostSuggestion) {
        e.preventDefault();
        acceptSuggestion();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void onSend();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (activeSuggestion) {
          rejectSuggestion();
        } else {
          setDraft('');
        }
      }
    },
    [
      onSend,
      ghostSuggestion,
      activeSuggestion,
      acceptSuggestion,
      rejectSuggestion,
      cannedPickerOpen,
    ],
  );

  const onChangeDraft = useCallback(
    (value: string) => {
      // S2 Task 8 — "/" digitado a partir do draft VAZIO abre o picker
      // (checado ANTES do setDraft — `draft` no closure ainda é o valor
      // anterior a esta mudança). Gated pela cap (sem ela, "/" é só texto).
      if (hasCannedCap && draft === '' && value.startsWith('/')) {
        setCannedPickerOpen(true);
      }
      setDraft(value);
      // Digitar por cima descarta sugestão silenciosamente (`ignored`) —
      // vale tanto para sugestão com texto quanto chip-only (`activeSuggestion`).
      if (activeSuggestion && value.length > 0 && value !== activeSuggestion.proposedText) {
        void aiSuggestion.recordDecision('ignored');
      }
    },
    [activeSuggestion, aiSuggestion, draft, hasCannedCap],
  );

  // r7b §32.3 — ConflictAlert critical bloqueia Composer (somente leitura).
  // ADR-117: severity=critical → blocksComposer='read-only'.
  const criticalConflict = conflictAlerts.alerts.find((a) => a.severity === 'critical') ?? null;
  const isCriticalConflictLocked = criticalConflict !== null;

  if (isClosed) {
    // A nota dizia SEMPRE «para reabrir, ação de supervisor necessária» — falso em
    // `archived`, onde nenhuma ação reabre: o endpoint executa `reopenSame` (exige
    // `isClosing`) e a matriz do ADR-054 não deixa `archived` transicionar. O caminho real
    // ali é o cliente escrever: `findOrCreateActiveConversation` cria conversa NOVA.
    // (Copy sujeita a veredito do TL — o que se consertou aqui é a afirmação falsa.)
    return (
      <div className="composer-r7b__locked border-t border-border-subtle bg-surface px-6 py-4 text-center text-sm text-text-secondary">
        {isReopenableStatus(conversation.status)
          ? 'Conversa encerrada. Para reabrir, ação de supervisor necessária.'
          : 'Conversa arquivada — o arquivamento é definitivo. Uma nova mensagem do cliente abre outra conversa.'}
      </div>
    );
  }

  if (isCriticalConflictLocked) {
    return (
      <div
        className="composer-r7b__locked composer-r7b__locked--pulse border-t border-danger-border bg-danger-bg"
        role="alert"
        data-testid="composer-conflict-locked"
        style={{
          background: 'var(--danger-bg)',
          borderTopColor: 'var(--danger-fg)',
        }}
      >
        <span className="shrink-0" style={{ color: 'var(--danger-fg)' }} aria-hidden="true">
          🔒
        </span>
        <div className="flex-1 text-xs" style={{ color: 'var(--danger-fg)' }}>
          <strong>Composer em modo somente leitura.</strong> ConflictAlert crítico bloqueia
          composição até mitigação.
          <span className="block mt-0.5 font-mono opacity-70 text-2xs">
            severity=critical · category={criticalConflict.category} · ADR-117 §32.3
          </span>
        </div>
      </div>
    );
  }

  if (isHcLocked) {
    return <HcLockedBanner conversation={conversation} />;
  }

  // PR-B.2 (Item 89) — banner sequence só renderiza quando há sequence ativa
  // com posição ≥ 2 (1ª conversa não é "em sequência" para o atendente).
  const showSequenceBanner =
    sequence.status === 'ready' &&
    sequence.position !== null &&
    sequence.total !== null &&
    sequence.windowHours !== null &&
    sequence.position >= 2;

  // PR-B.2 — fallback ConflictWarningBanner para alerts ativos sem sugestão IA
  // em curso. O canal canônico (Fatia 7 — ai.suggestion.blocked-by-conflict / warning)
  // tem prioridade; este apenas cobre "modo redação manual" enquanto não há
  // sugestão pendente.
  const fallbackConflict =
    aiSuggestion.blocked === null && aiSuggestion.suggestion?.warning === undefined
      ? (conflictAlerts.alerts[0] ?? null)
      : null;

  return (
    <div className="composer-r7b">
      {showSequenceBanner && (
        <SequenceBanner
          position={sequence.position ?? 0}
          total={sequence.total ?? 0}
          windowHours={sequence.windowHours ?? 0}
          flags={sequence.flags}
        />
      )}

      <ChecklistAgentBanner conversationId={conversation.id} />

      {/* Item 27 — janela 24h Meta fechada: aviso advisory + porta de entrada
          do TemplateSelectorModal. Estados que suprimem o Composer (closed /
          conflito crítico / H_c) retornam antes deste JSX — banner some junto. */}
      {window24h.state === 'closed' && (
        <Window24hBanner
          reason={window24h.reason}
          onOpenSelector={() => setTemplateModalOpen(true)}
        />
      )}

      {/* Linha 1 canonical — ClassificationSeal + PlaybookPill rich + DelegateAction */}
      <div className="composer-r7b__contextbar">
        <ClassificationSeal level={conversation.classificationLevel ?? 1} />
        <PersonaPlaybookPill conversation={conversation} />
        <AdversarialBadge adversarial={adversarial} />
        {sequence.status === 'ready' && sequence.position !== null && sequence.total !== null && (
          <SequenceBadge
            position={sequence.position}
            total={sequence.total}
            highVolume={sequence.flags.highVolumeFlag}
            escalating={sequence.flags.escalatingSentimentFlag}
          />
        )}
        <SuggestionStatusBadge
          status={aiSuggestion.status}
          errorReason={aiSuggestion.errorReason}
          errorMessage={aiSuggestion.errorMessage}
        />
        <div className="composer-r7b__contextbar__spacer" />
        <DelegateStepsAction conversation={conversation} autoOpen={autoOpenDelegateSteps} />
        <DelegateAction conversation={conversation} />
      </div>

      <ConflictWarningBanner
        blocked={aiSuggestion.blocked}
        warning={aiSuggestion.suggestion?.warning}
        fallbackAlert={fallbackConflict}
      />

      {/* Sprint Protection v1 Agent C (Item 226) — Banner inline pré-envio
          quando severity ∈ {low, medium} + action='warn'. Aparece após sucesso
          do sendOutbound; atendente vê warning antes do próximo envio. */}
      {protection.uiDecision === 'banner' && protection.validation !== null && (
        <ProtectionWarningBanner validation={protection.validation} onDismiss={protection.clear} />
      )}

      {/* S2 Task 9 — fila de revisão real, bloco IRMÃO ACIMA do
          `.composer-r7b__box` abaixo (veredito visual TL) — o
          composer/textarea seguem plenamente operáveis com a fila montada (a
          fila NUNCA sequestra a conversa). `key={stagedResponse.id}` força
          REMOUNT completo se o atendente selecionar OUTRA resposta pronta
          enquanto uma já está em revisão (reseta burstKey/itens — nunca
          mistura duas rajadas). Loading gate declarado: enquanto
          `cannedVariablesQ` ainda está buscando (nem sucesso nem erro
          assentou), NÃO monta a fila — evita um flash falso de "tudo âmbar"
          com `variables={{}}` antes dos valores reais chegarem. Em erro,
          monta mesmo assim com `{}` (mesmo efeito seguro de "não resolvido"
          — nunca finge sucesso). */}
      {stagedResponse !== null && stagedConflictNotice && (
        <div
          role="status"
          data-testid="review-queue-conflict-notice"
          className="mb-1 rounded px-3 py-1.5 text-xs"
          style={{
            background: 'var(--warning-bg)',
            color: 'var(--warning-fg)',
            border: '1px solid var(--warning-border)',
          }}
        >
          Descarte ou envie a sequência atual antes de escolher outra resposta.
        </div>
      )}
      {stagedResponse !== null && !cannedVariablesQ.isLoading && (
        <ReviewQueue
          key={stagedResponse.id}
          conversationId={conversation.id}
          channel={conversation.channel}
          response={stagedResponse}
          variables={cannedVariablesQ.data?.values ?? {}}
          verified={conversation.identity?.verified === true}
          onDismiss={dismissStagedResponse}
        />
      )}

      {/* Linha 2+3 canonical — composer-r7b__box com strip rica AI integrada + textarea com ghost text inline.
          `relative` ancora o CannedResponsePicker (painel absoluto acima do box, S2 Task 8). */}
      <div className="composer-r7b__box relative">
        <SuggestionStrip suggestion={ghostSuggestion} />
        {aiSuggestion.status === 'loading' && <GeneratingStrip />}
        <div className="relative">
          {ghostSuggestion !== null && draft === '' && (
            <div
              className="pointer-events-none absolute inset-0 px-3 py-2 text-sm italic whitespace-pre-wrap"
              aria-hidden="true"
              data-testid="ai-suggestion-ghost"
              style={{ color: 'var(--fg-3)' }}
            >
              {ghostSuggestion.proposedText}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => onChangeDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            placeholder={
              ghostSuggestion === null
                ? 'Digite sua mensagem… (Cmd/Ctrl+Enter envia, Esc limpa)'
                : ''
            }
            disabled={isSending}
            readOnly={aiSuggestion.status === 'loading'}
            className="composer-r7b__textarea"
            aria-label="Compor mensagem"
            // Important #2 pós-review — wire ARIA mínimo de combobox enquanto
            // o picker está aberto (atributos DINÂMICOS, sem role permanente;
            // o role acompanha: ARIA 1.2 só suporta aria-expanded em combobox,
            // não no textbox implícito — jsx-a11y/role-supports-aria-props):
            // o leitor de tela anuncia o listbox controlado + a option ativa.
            role={cannedPickerOpen ? 'combobox' : undefined}
            aria-expanded={cannedPickerOpen ? true : undefined}
            aria-controls={cannedPickerOpen ? 'canned-picker-listbox' : undefined}
            aria-activedescendant={
              cannedPickerOpen && cannedActiveOptionId !== null ? cannedActiveOptionId : undefined
            }
          />
        </div>
        {/* T9 Fase 1 «um cérebro só» — chips de conduta abaixo do texto
            fantasma. Sem chips (v1/só-texto) a linha não existe (byte-idêntico).
            Fix 2 (revisão final Fase 1) — usa `activeSuggestion` (não
            `ghostSuggestion`): chips renderizam mesmo quando a sugestão é
            chip-only (`proposedText` vazio, sem ghost text/Tab-aceita). */}
        {activeSuggestion !== null && (
          <SuggestionChipsRow
            chips={activeSuggestion.chips}
            className="composer-r7b__chips"
            {...(chipOtpAvailable && { onOtpChallenge: () => setConfirmTitularOpen(true) })}
            {...(!isClosed && { onCloseConversation: closeFlow.openClose })}
            {...(canEstablishLink && { onLinkCustomer: () => setEstablishLinkOpen(true) })}
          />
        )}
        <div className="composer-r7b__toolbar">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={isSending}
            className="composer-r7b__icon-btn"
            aria-label="Anexar arquivo"
            title={
              attachedDocument !== null ? `Anexado: ${attachedDocument.name}` : 'Anexar arquivo'
            }
            style={attachedDocument !== null ? { background: 'var(--brand-subtle)' } : undefined}
          >
            <Paperclip className="icon" size={16} aria-hidden />
            {attachedDocument !== null && <span style={{ fontSize: 10, marginLeft: 2 }}>•</span>}
          </button>
          {/* S2 Task 8 — Respostas Prontas: botão ⚡ abre o picker em modo
              navegação (query vazia). Gated `canned-response-use` — some do
              DOM sem a cap (atalho "/" também é gated, na `onChangeDraft`). */}
          {hasCannedCap && (
            <button
              type="button"
              onClick={() => setCannedPickerOpen((v) => !v)}
              disabled={isSending}
              className="composer-r7b__icon-btn"
              aria-label="Respostas prontas"
              title="Respostas prontas (/)"
              style={cannedPickerOpen ? { background: 'var(--brand-subtle)' } : undefined}
              data-testid="canned-picker-toggle"
            >
              <Zap className="icon" size={16} aria-hidden />
            </button>
          )}
          <ComposerIconBtn Icon={Mic} label="Gravar áudio" />
          <ComposerIconBtn Icon={Smile} label="Inserir emoji" />
          <ComposerIconBtn Icon={Hash} label="Inserir hashtag" />
          <div className="composer-r7b__toolbar-spacer" />
          {ghostSuggestion !== null && (
            <>
              <button
                type="button"
                onClick={rejectSuggestion}
                className="btn btn--ghost btn--sm"
                aria-label="Recusar sugestão"
                title="Recusar sugestão (Esc)"
              >
                Recusar
              </button>
              <button
                type="button"
                onClick={acceptSuggestion}
                className="btn btn--ai btn--sm"
                aria-label="Aceitar sugestão"
                title="Aceitar sugestão (Tab)"
              >
                Aceitar
              </button>
            </>
          )}
          {/* Task 2 — botão "Sugerir": visível em H_a + atendente atribuído +
              sem sugestão ativa (texto OU chip-only — `activeSuggestion`) +
              não em loading. Reusa btn--ai (mesmo visual dos botões de
              sugestão existentes). Tooltip em PT-BR conforme spec. */}
          {mode === 'H_a' &&
            conversation.assignedToUserId !== null &&
            activeSuggestion === null &&
            aiSuggestion.status !== 'loading' && (
              <button
                type="button"
                onClick={() => void aiSuggestion.requestManual()}
                className="btn btn--ai btn--sm"
                aria-label="Pedir uma sugestão da IA"
                title="Pedir uma sugestão da IA"
              >
                Sugerir
              </button>
            )}
          {/* Task 2 — mensagem de erro sutil quando requestManual falha (409 guard etc.). */}
          {aiSuggestion.status === 'error' && aiSuggestion.errorMessage !== null && (
            <span
              className="text-2xs"
              style={{ color: 'var(--warning-fg)' }}
              data-testid="ai-suggestion-error-message"
              title={aiSuggestion.errorMessage}
            >
              <span aria-hidden>⚠</span> {aiSuggestion.errorMessage}
            </span>
          )}
          <CharCounter charsLeft={charsLeft} isOversized={isOversized} totalChars={draft.length} />
          <button
            type="button"
            onClick={() => void onSend()}
            disabled={!canSend}
            className="btn btn--primary btn--sm"
            aria-label="Enviar mensagem"
            title={canSend ? 'Enviar (Cmd/Ctrl+Enter)' : 'Digite uma mensagem para habilitar'}
          >
            {isSending ? 'Enviando…' : 'Enviar'}
          </button>
        </div>

        {/* S2 Task 8 — painel de busca/seleção de Respostas Prontas, aberto
            pelo atalho "/" (draft vazio) ou pelo botão ⚡/chip "Biblioteca…".
            NUNCA envia — onSelect alimenta `stagedResponse` (fila de revisão
            da Task 9). */}
        {cannedPickerOpen && (
          <CannedResponsePicker
            query={cannedPickerQuery}
            playbookId={activePlaybookId}
            playbookName={conversation.playbook?.name ?? null}
            onSelect={handleCannedSelect}
            onClose={() => {
              setCannedPickerOpen(false);
              textareaRef.current?.focus();
            }}
            onActiveDescendantChange={setCannedActiveOptionId}
          />
        )}
      </div>

      {/* Linha 4 — chips reais de Respostas Prontas do playbook ativo (S2 Task 8;
          as pills fake `QuickRepliesPills` morreram ali).

          O link `/atalhos` que vivia aqui SAIU (14/Ago, achado do TL: «está
          ocupando um trecho da tela sem porquê»). Três motivos, todos verificáveis:
          ele abria o MESMO modal que o `⌘/` do menu `⋯` do header já abre; o
          `<div>` que o embrulhava renderizava SEMPRE, então sem chips sobrava uma
          faixa com um link solto à direita; e o Brief 2A V2.3 §1.2 manda
          literalmente «Composer sem faixa de atalhos persistente. Atalhos vivem no
          modal ⌘/» — tirar é voltar ao briefing, não desviar dele.

          `CannedChipsRow` já retorna `null` sozinho (sem cap, sem playbook, ou zero
          respostas vinculadas), então a linha some inteira nesses casos. */}
      <CannedChipsRow
        playbookId={activePlaybookId}
        onSelect={handleCannedSelect}
        onOpenLibrary={() => setCannedPickerOpen(true)}
      />

      {/* Sprint Protection v1 Agent C (Item 226) + F1.3 (bloqueio honesto) —
          Modal INFORMATIVO quando severity ∈ {high, critical} + action='block'.
          Mensagem permanece preservada no draft; o atendente edita e reenvia.
          NÃO há "Enviar mesmo assim" — o bloqueio é HARD no Engine. */}
      <ProtectionBlockModal
        open={protection.uiDecision === 'modal'}
        validation={protection.validation}
        onCancel={onProtectionCancel}
      />

      {/* Item 36 (PR-B2) — DocumentPicker modal para seleção de anexo.
          Item 236 (29/Mai/2026) — recebe DocumentSummary completo para
          montar card otimista no bubble outbound. */}
      <DocumentPicker
        open={pickerOpen}
        bondId={bondId}
        onClose={() => setPickerOpen(false)}
        onConfirm={(doc) => {
          setAttachedDocument(doc);
        }}
      />

      {/* Item 27 — seletor de template HSM (janela 24h fechada). */}
      <TemplateSelectorModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSend={(req) => void onSendTemplate(req)}
        isSending={isSending}
      />

      {/* T9 chip `otp-challenge` — MESMO fluxo Confirmar titular do popover de
          identidade (Item 391-B; o modal se auto-gateia por CapabilityGate). */}
      {chipOtpAvailable && (
        <ConfirmCustomerIdentityModal
          conversationId={conversation.id}
          personName={conversation.person?.displayName ?? '(sem identidade)'}
          open={confirmTitularOpen}
          onClose={() => setConfirmTitularOpen(false)}
        />
      )}

      {/* F2-b chip `link-customer` — MESMO EstablishLinkModal do empty-state da
          aba Cliente (F2-a); o modal se auto-gateia por CapabilityGate. */}
      {canEstablishLink && (
        <EstablishLinkModal
          conversationId={conversation.id}
          personName={conversation.person?.displayName ?? '(sem identidade)'}
          open={establishLinkOpen}
          onClose={() => setEstablishLinkOpen(false)}
        />
      )}

      {/* T9 chip `close` — MESMO fluxo Encerrar do header (hook compartilhado
          `useCloseConversationFlow`; renderiza null quando fechado). */}
      <ConversationCloseModal
        open={closeFlow.closeOpen}
        onClose={closeFlow.dismissClose}
        onSubmit={closeFlow.handleCloseSubmit}
        submitting={closeFlow.isClosing}
        followupEnabled={canFollowup}
        {...(closeFlow.closeBlocker !== undefined ? { blockerError: closeFlow.closeBlocker } : {})}
      />
    </div>
  );
}

function SuggestionStrip({
  suggestion,
}: {
  suggestion: { proposedText: string } | null;
}): JSX.Element | null {
  if (!suggestion) return null;
  return (
    /* Brief 2A r7b — classe canonical .composer-r7b__strip (global.css §754-782) renderiza
       como header DENTRO do composer-r7b__box com border-bottom + radius top-only. */
    <div className="composer-r7b__strip" data-testid="ai-suggestion-strip">
      <span className="font-medium uppercase tracking-wide">Sugestão da IA</span>
      <span className="composer-r7b__strip__spacer" />
      <span className="composer-r7b__strip__hint">
        Tab aceita · Esc recusa · Digite para ignorar
      </span>
    </div>
  );
}

/**
 * GeneratingStrip — Brief 2A spec §4.2 (estado `generating`).
 *
 * Strip no topo do composer-r7b__box enquanto `suggestion-agent` gera. Pulse +
 * `aria-live="polite"` para anunciar a leitores de tela. Em paralelo a textarea
 * fica readonly (não-edição enquanto IA gera).
 */
function GeneratingStrip(): JSX.Element {
  return (
    <div
      className="composer-r7b__strip"
      data-testid="ai-generating-strip"
      role="status"
      aria-live="polite"
    >
      <span className="font-medium uppercase tracking-wide animate-pulse">
        IA está gerando sugestão…
      </span>
    </div>
  );
}

function SuggestionStatusBadge({
  status,
  errorReason,
  errorMessage,
}: {
  status: 'idle' | 'loading' | 'ready' | 'error' | 'blocked';
  errorReason: string | null;
  /** Item 482 — motivo de NEGÓCIO vindo do REST (sem sugestão ≠ IA indisponível). */
  errorMessage?: string | null;
}): JSX.Element | null {
  // `loading` agora é o GeneratingStrip canônico (§4.2) dentro do box;
  // `ready`/`blocked` têm banner próprio. Badge da contextbar só cobre `error`.
  if (status === 'idle' || status === 'ready' || status === 'blocked' || status === 'loading') {
    return null;
  }
  return (
    /* Fase C Brief 2A r7b — DS warning token (--warning-fg). */
    <span
      className="text-2xs inline-flex items-center gap-1"
      style={{ color: 'var(--warning-fg)' }}
      data-testid="ai-suggestion-error"
      title={
        errorMessage
          ? `Sem sugestão: ${errorMessage}`
          : `IA temporariamente indisponível${errorReason ? `: ${errorReason}` : ''}`
      }
    >
      <span aria-hidden>⚠</span> {errorMessage ? 'Sem sugestão' : 'IA indisp.'}
    </span>
  );
}

function CharCounter({
  charsLeft,
  isOversized,
  totalChars,
}: {
  charsLeft: number;
  isOversized: boolean;
  totalChars: number;
}): JSX.Element | null {
  // Mostra contador apenas quando aproximando do limite (< 200 chars restantes).
  if (totalChars < MAX_CHARS - 200 && !isOversized) return null;
  return (
    /* Fase C Brief 2A r7b — DS danger token quando oversized. */
    <span
      className="text-2xs"
      style={isOversized ? { color: 'var(--danger-fg)', fontWeight: 500 } : undefined}
      aria-live="polite"
    >
      {isOversized
        ? `${(-charsLeft).toString()} acima do limite`
        : `${charsLeft.toString()} restantes`}
    </span>
  );
}

/**
 * PersonaPlaybookPill canonical — Brief 2A r7b Linha 1 do Composer.
 *
 * Pill exibindo Persona + Playbook ATIVOS reais da conversa (detail do
 * Engine — playbookId FK + persona resolvida). Some quando a conversa não
 * tem nenhum dos dois (honesto — sem dado fake de protótipo). Display-only;
 * configuração inline é fatia futura.
 */
function PersonaPlaybookPill({
  conversation,
}: {
  conversation: ConversationDetail;
}): JSX.Element | null {
  const personaName = conversation.persona?.name ?? null;
  const playbookName = conversation.playbook?.name ?? null;
  if (personaName === null && playbookName === null) return null;
  return (
    <button
      type="button"
      disabled
      className="composer-r7b__pill"
      title="Persona + Playbook ativos — configuração inline em fatia futura"
    >
      <span className="composer-r7b__pill-text">
        {personaName !== null && <b>{personaName}</b>}
        {personaName !== null && playbookName !== null && (
          <span aria-hidden style={{ color: 'var(--fg-3)' }}>
            ·
          </span>
        )}
        {playbookName !== null && <span>{playbookName}</span>}
      </span>
    </button>
  );
}

/**
 * HcLockedBanner canonical — Composer em modo H_c (IA dirigindo).
 *
 * Substitui canonical o Composer normal quando conversation está em H_c.
 * Botão "Retomar" canonical wireado com `useSetOperationMode('H_a')` para
 * voltar canonical ao modo humano dirige.
 *
 * ⚠️ Wave 2 canonical (Item 223 Auditoria #58): `conductor-agent` ainda é
 * stub canonical — não materializado. Em H_c, IA deveria conduzir
 * autonomamente mas atualmente nada acontece (zero responses canonical).
 * Sprint Backend Protection v1 canonical materializa conductor-agent.
 */
function HcLockedBanner({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const setOpMode = useSetOperationMode(conversation.id);
  const onClick = (): void => {
    if (setOpMode.isPending) return;
    void setOpMode.mutate('H_a');
  };
  return (
    <div
      className="composer-r7b__locked border-t border-border-subtle"
      style={{ background: 'var(--ai-bg)' }}
    >
      <div className="flex items-center gap-2 text-xs flex-1">
        <span
          className="text-2xs uppercase tracking-wide font-medium"
          style={{ color: 'var(--ai-fg)' }}
        >
          IA dirigindo
        </span>
        <span style={{ color: 'var(--fg-2)' }}>
          A IA está conduzindo. Use "Retomar" para voltar ao modo manual.
        </span>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={setOpMode.isPending}
        className="text-xs px-2 py-1 rounded border"
        style={{
          borderColor: 'var(--ai-border)',
          color: 'var(--ai-fg)',
          cursor: setOpMode.isPending ? 'wait' : 'pointer',
        }}
        title={setOpMode.isPending ? 'Retomando…' : 'Retomar conversa (voltar para H_a)'}
      >
        {setOpMode.isPending ? 'Retomando…' : 'Retomar'}
      </button>
    </div>
  );
}

/**
 * DelegateStepsAction — S7 PR1 (§4.3, Decisão 2).
 *
 * Ação SECUNDÁRIA ao lado de `DelegateAction` — NÃO substitui o "Delegar à
 * IA" existente (que flipa H_a→H_c inteiro). Abre o `DelegateStepsModal`
 * (S5, ADR-154: delegar um CONJUNTO de objetivos + timeout de segurança,
 * com devolução determinística). Gated `ai-step-delegation` — inerte
 * (botão nem renderiza) quando a cap está OFF, o que hoje é o default
 * (S5 gated OFF) ⇒ merge desta PR não muda produção.
 *
 * S5 (maré baixa produtiva, Item 405) — o gesto "delegar à IA" do card de
 * sugestão navega para cá com `?delegate=1`. O PARAM é consumido no
 * Composer (que roda hooks em todos os modos — este componente nem monta em
 * H_c); aqui só chega o sinal `autoOpen`, que abre este MESMO modal (zero
 * máquina nova — só um gatilho a mais para o `open` existente).
 */
function DelegateStepsAction({
  conversation,
  autoOpen = false,
}: {
  conversation: ConversationDetail;
  autoOpen?: boolean;
}): JSX.Element | null {
  const hasCap = useCapability('ai-step-delegation');
  const [open, setOpen] = useState(false);
  const mode = conversation.operationMode.current;

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  if (!hasCap) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={mode === 'H_c'}
        className="btn btn--ghost btn--sm"
        style={{
          borderColor: 'var(--ai-border)',
          color: 'var(--ai-fg)',
        }}
        title={
          mode === 'H_c'
            ? 'IA já está conduzindo'
            : 'Delegar um conjunto de objetivos à IA, com devolução automática'
        }
        data-testid="delegate-steps-action"
      >
        Delegar etapas…
      </button>
      <DelegateStepsModal conversation={conversation} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * DelegateAction canonical — Brief 2A r7b Linha 1 (right side).
 *
 * Botão "Delegar à IA" canonical — alterna modo operacional para H_c (IA dirige
 * autonomamente). Wireado via `useSetOperationMode` (Task 59). Quando já está
 * em H_c, botão fica desabilitado com label "IA dirigindo".
 *
 * Wave 2: bloqueio canonical quando `protection-agent` não-materializado
 * (Item 223 Auditoria #58) — v1 permite alternar mas IA pode self-escalate
 * via token `__PROTECTION_ESCALATE__`.
 */
function DelegateAction({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const mode = conversation.operationMode.current;
  const setOpMode = useSetOperationMode(conversation.id);
  const isHc = mode === 'H_c';

  const onClick = (): void => {
    if (isHc || setOpMode.isPending) return;
    void setOpMode.mutate('H_c');
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isHc || setOpMode.isPending}
      className="btn btn--ghost btn--sm"
      style={{
        borderColor: 'var(--ai-border)',
        color: 'var(--ai-fg)',
        background: 'var(--ai-bg)',
      }}
      title={
        isHc
          ? 'IA já está conduzindo — retomar via ModeBadge no header'
          : setOpMode.isPending
            ? 'Alternando para H_c…'
            : 'Delegar conversa para a IA conduzir autonomamente (H_c)'
      }
    >
      <Bot
        className="icon"
        size={14}
        aria-hidden
        style={{ display: 'inline', verticalAlign: '-2px' }}
      />{' '}
      {isHc ? 'IA dirigindo' : setOpMode.isPending ? 'Delegando…' : 'Delegar à IA'}
    </button>
  );
}

/**
 * ComposerIconBtn canonical — Brief 2A r7b Linha 3 toolbar.
 *
 * Icon-button compacto (26x26) na barra inferior do .composer-r7b__box:
 * anexar / áudio / emoji / hashtag. Todos disabled v1 (Fatia 6 PR-B
 * materializa anexar Document funcional + snippets pessoais; áudio/emoji
 * em fatia futura).
 */
function ComposerIconBtn({ Icon, label }: { Icon: LucideIcon; label: string }): JSX.Element {
  return (
    <button
      type="button"
      disabled
      className="composer-r7b__icon-btn"
      title={`${label} — disponível em fatia futura`}
      aria-label={label}
    >
      <Icon className="icon" size={16} aria-hidden />
    </button>
  );
}

/**
 * Item 236 (29/Mai/2026) — converte DocumentSummary (do DocumentPicker) em
 * AttachedDocument parcial para o optimistic update. signedUrl/signedUrlExpiresAt
 * ficam undefined — só chegam via envelope no onSuccess do mutation.
 */
function documentSummaryToAttached(doc: DocumentSummary): AttachedDocument {
  return {
    id: doc.id,
    name: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
  };
}
